import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, Loader2, PenLine, Reply, Send } from 'lucide-react';

import {
  createDraft,
  getMailbox,
  saveDraftToOutlook,
  suggestReply,
} from '../../services/mailIntelApi';
import ReplyForm from './ReplyForm';
import { Btn, Chip, SectionLabel, T, fmtDateTime, humanise } from './shared';

const MAX_RAW = 4000;
const SENTENCE = /(?<=[.!?])\s+|\n+/;
const BRACKET = /(\[[^\]]*\])/g;

const roleName = (r) =>
  ({ pm: 'PM', ba: 'BA', tl: 'Tech Lead' })[r] || (r ? humanise(r) : null);

/** Body with [bracketed] values highlighted and each sentence's trace on hover. */
const DraftPreview = ({ body, trace }) => {
  const sentences = body.split(SENTENCE).filter((s) => s.trim());
  return (
    <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-100">
      {sentences.map((sentence, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          title={trace?.[i]?.source ? `Source: ${trace[i].source}` : undefined}
          className="cursor-help hover:bg-zinc-800/80"
        >
          {sentence.split(BRACKET).map((part, j) =>
            part.startsWith('[') ? (
              <mark
                // eslint-disable-next-line react/no-array-index-key
                key={j}
                className="rounded bg-amber-400/20 px-0.5 text-amber-200"
              >
                {part}
              </mark>
            ) : (
              part
            ),
          )}{' '}
        </span>
      ))}
    </p>
  );
};

/**
 * Reply to a mail (PRD §8). A suggested reply is offered as soon as the
 * thread is open; the user can instead type their own points and get a
 * Recommended draft and one variant. "Reply" puts the chosen text into the
 * reply form (To, Cc, Subject, text), and nothing is sent until the user
 * presses Send there. Copy and Save to Outlook drafts stay available.
 */
const DraftPanel = ({ accessToken, thread }) => {
  // Only mail in the viewer's own mailbox, from the other side, can be answered.
  const targets = (thread.mails || []).filter((m) => m.mine && !m.ours);
  const [targetId, setTargetId] = useState(targets.at(-1)?.id || '');
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [active, setActive] = useState(0);
  const [bodies, setBodies] = useState([]);
  const [editing, setEditing] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [canSend, setCanSend] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [sent, setSent] = useState(null);
  // True once the user asked for a draft from their own points.
  const userDrafted = useRef(false);

  useEffect(() => {
    getMailbox(accessToken)
      .then((res) => {
        setCanSave(!!res?.data?.can_save_drafts);
        setCanSend(!!res?.data?.can_send);
      })
      .catch(() => {
        setCanSave(false);
        setCanSend(false);
      });
  }, [accessToken]);

  // A suggested reply for the mail being answered, offered without asking.
  // The backend writes it once per mail, so coming back here is free.
  const threadId = thread.thread_id;
  useEffect(() => {
    if (!targetId) return undefined;
    let current = true;
    setSuggesting(true);
    suggestReply(accessToken, threadId, targetId)
      .then((res) => {
        // Never replace a draft the user asked for meanwhile.
        if (!current || userDrafted.current) return;
        if (!res?.data?.variants?.length) return;
        setDraft(res.data);
        setBodies(res.data.variants.map((v) => v.body));
        setActive(0);
      })
      // No suggestion (drafting off, model down): the points box still works.
      .catch(() => {})
      .finally(() => current && setSuggesting(false));
    return () => {
      current = false;
    };
  }, [accessToken, threadId, targetId]);

  if (!targets.length) {
    return (
      <p className="text-[13px] text-zinc-400">
        You can draft replies only to mail in your own mailbox.
      </p>
    );
  }

  const generate = async () => {
    userDrafted.current = true;
    setLoading(true);
    setSaved(null);
    setSent(null);
    setReplying(false);
    try {
      const res = await createDraft(
        accessToken,
        thread.thread_id,
        targetId,
        raw.trim(),
      );
      setDraft(res?.data);
      setBodies((res?.data?.variants || []).map((v) => v.body));
      setActive(0);
      setEditing(false);
    } catch (error) {
      toast.error(`Could not draft: ${error?.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const variant = draft?.variants?.[active];
  const body = bodies[active] ?? '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success('Draft copied');
    } catch {
      toast.error('Could not copy. Select the text and copy it.');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveDraftToOutlook(accessToken, draft.id, active, body);
      setSaved(res?.data);
      toast.success('Saved to your Outlook drafts');
    } catch (error) {
      if (error?.code === 'RECONNECT_REQUIRED') setCanSave(false);
      toast.error(`Could not save: ${error?.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {targets.length > 1 && (
        <select
          aria-label="Mail to reply to"
          value={targetId}
          onChange={(e) => {
            // Another mail: its own suggestion is fetched by the effect above.
            userDrafted.current = false;
            setTargetId(e.target.value);
            setDraft(null);
            setBodies([]);
            setReplying(false);
            setSent(null);
            setSaved(null);
          }}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100"
        >
          {targets.map((m) => (
            <option key={m.id} value={m.id}>
              {fmtDateTime(m.at)} · {humanise(m.mail_type)}
            </option>
          ))}
        </select>
      )}
      <textarea
        aria-label="What do you want to say?"
        value={raw}
        onChange={(e) => setRaw(e.target.value.slice(0, MAX_RAW))}
        rows={3}
        placeholder="Your points in your own words, e.g. fix is in QA, aim for Friday, I'll confirm tomorrow"
        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 p-2.5 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF]"
        disabled={loading}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-zinc-500">
          The draft states only what you write here; anything else is put in
          [brackets] for you to check.
        </span>
        <Btn
          variant="primary"
          onClick={generate}
          disabled={loading || !raw.trim() || !targetId}
        >
          <span className="flex items-center gap-1.5">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <PenLine className="h-3.5 w-3.5" />
            )}
            {loading ? 'Writing drafts…' : 'Draft reply'}
          </span>
        </Btn>
      </div>
      {loading && (
        <p className="text-[12px] text-zinc-400">
          This can take up to a minute while the drafts are checked.
        </p>
      )}
      {suggesting && !draft && !loading && (
        <p className="flex items-center gap-1.5 text-[12px] text-zinc-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Preparing a suggested reply…
        </p>
      )}

      {variant && (
        <div className="rounded-xl border border-zinc-700/60 bg-zinc-800/30 p-3">
          {draft.suggested && (
            <p className="mb-2 text-[12px] text-zinc-300">
              <span className="font-semibold text-[#9b98f5]">
                Suggested reply.
              </span>{' '}
              Written from the thread and the confirmed items, without points
              from you. Type your points above for a draft that says what you
              want.
            </p>
          )}
          <div
            className="mb-2 flex flex-wrap items-center gap-2"
            role="tablist"
          >
            {draft.variants.map((v, i) => (
              <button
                key={v.label}
                type="button"
                role="tab"
                aria-selected={i === active}
                onClick={() => {
                  setActive(i);
                  setSaved(null);
                  setReplying(false);
                }}
                className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${
                  i === active
                    ? 'bg-[#5652CF] text-white'
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                {v.label}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-zinc-400">
              To {draft.recipients?.to}
              {draft.recipients?.cc?.length
                ? ` · CC ${draft.recipients.cc.join(', ')}`
                : ''}
            </span>
          </div>

          {draft.scope_note && (
            <p className="mb-2 text-[12px] text-amber-300">
              No commitments in this draft: {draft.scope_note}. Dates and
              promises you typed are in [brackets] for you to confirm.
            </p>
          )}
          {draft.limited_context && (
            <p className="mb-2 text-[12px] text-amber-300">
              Limited context: this thread is not tracked, so the draft uses
              only your points and the mail you answer.
            </p>
          )}
          {draft.untold_changes?.length > 0 && (
            <div className="mb-2 rounded-lg border border-sky-900/60 bg-sky-950/30 p-2 text-[12px] text-sky-200">
              <p className="font-semibold">
                Not yet told to {draft.recipients?.to}
              </p>
              {draft.untold_changes.map((c) => (
                <p key={c.item_id}>
                  {c.text} ({c.changed.join(', ')})
                </p>
              ))}
              <p className="text-sky-300/80">
                Decide whether this reply should mention them.
              </p>
            </div>
          )}
          {variant.subject && (
            <p className="mb-1 text-[12px] text-zinc-400">{variant.subject}</p>
          )}
          {editing ? (
            <textarea
              aria-label="Draft text"
              value={body}
              onChange={(e) =>
                setBodies(
                  bodies.map((b, i) => (i === active ? e.target.value : b)),
                )
              }
              rows={8}
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 p-2.5 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF]"
            />
          ) : (
            <DraftPreview body={body} trace={variant.trace} />
          )}

          {variant.open_decisions?.length > 0 && (
            <div className="mt-3">
              <SectionLabel count={variant.open_decisions.length}>
                Open decisions
              </SectionLabel>
              <ul className="flex flex-col gap-1">
                {variant.open_decisions.map((d) => (
                  <li
                    key={`${d.kind}-${d.item}`}
                    className="flex items-center gap-2 text-[12px] text-zinc-200"
                  >
                    <Chip
                      fg={d.kind === 'unaddressed_ask' ? T.info : T.warn}
                      bg={d.kind === 'unaddressed_ask' ? T.infoBg : T.warnBg}
                    >
                      {d.kind === 'unaddressed_ask'
                        ? 'Not answered'
                        : 'To confirm'}
                    </Chip>
                    {d.item}
                    {roleName(d.decision_owner_role) && (
                      <span className="text-zinc-500">
                        · {roleName(d.decision_owner_role)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {variant.warnings?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-300">
                Check before sending
              </p>
              <ul className="list-disc pl-4 text-[12px] text-amber-100">
                {variant.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Btn
              variant="primary"
              onClick={() => setReplying(true)}
              disabled={replying || !!sent || !body.trim()}
            >
              <span className="flex items-center gap-1.5">
                <Reply className="h-3.5 w-3.5" /> Reply
              </span>
            </Btn>
            <Btn onClick={() => setEditing(!editing)}>
              {editing ? 'Preview' : 'Edit'}
            </Btn>
            <Btn onClick={copy}>
              <span className="flex items-center gap-1.5">
                <Copy className="h-3.5 w-3.5" /> Copy
              </span>
            </Btn>
            <Btn onClick={save} disabled={!canSave || saving || !body.trim()}>
              <span className="flex items-center gap-1.5">
                <Send className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save to Outlook drafts'}
              </span>
            </Btn>
            {!canSave && (
              <span className="text-[11px] text-zinc-500">
                Reconnect your mailbox in Profile Settings to save to Outlook.
              </span>
            )}
          </div>
          {replying && !sent && (
            <ReplyForm
              // A fresh form for each draft and variant the user replies with.
              key={`${draft.id}-${active}`}
              accessToken={accessToken}
              threadId={thread.thread_id}
              targetId={targetId}
              draftId={draft.id}
              variant={active}
              canSend={canSend}
              initial={{
                to: draft.recipients?.to || '',
                cc: (draft.recipients?.cc || []).join(', '),
                body,
              }}
              onSent={(result) => {
                setSent(result || { sent: true });
                setReplying(false);
              }}
              onCancel={() => setReplying(false)}
            />
          )}
          {sent && (
            <p className="mt-2 text-[12px] text-emerald-300" role="status">
              Reply sent from your mailbox.
              {sent.recipients_joined?.length > 0 &&
                ` Also went to ${sent.recipients_joined.join(', ')}.`}
            </p>
          )}
          {saved && (
            <p className="mt-2 text-[12px] text-emerald-300">
              Saved to your Outlook drafts. Review and send it from Outlook.
              {saved.web_link && (
                <>
                  {' '}
                  <a
                    href={saved.web_link}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Open draft
                  </a>
                </>
              )}
              {saved.brackets_left && (
                <span className="block text-amber-300">
                  It still has [bracketed] values to fill in.
                </span>
              )}
              {(saved.tier_warnings || []).map((w) => (
                <span key={w} className="block text-amber-300">
                  {w}
                </span>
              ))}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DraftPanel;
